
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DollarSign, Calendar, FileText, Download } from 'lucide-react';

const PatientLedger = () => {
  const transactions = [
    {
      id: '1',
      date: '2024-01-15',
      type: 'treatment',
      description: 'Botox Treatment - 25 units',
      amount: 375.00,
      credits: -375.00,
      payment: 'Credits',
      balance: 275.50
    },
    {
      id: '2',
      date: '2024-01-15',
      type: 'purchase',
      description: 'Credit Package Purchase',
      amount: 500.00,
      credits: 500.00,
      payment: 'Credit Card ***4567',
      balance: 650.50
    },
    {
      id: '3',
      date: '2024-01-10',
      type: 'treatment',
      description: 'Dermaplaning Facial',
      amount: 150.00,
      credits: -150.00,
      payment: 'Credits',
      balance: 150.50
    },
    {
      id: '4',
      date: '2024-01-05',
      type: 'bonus',
      description: 'New Member Bonus Credits',
      amount: 100.00,
      credits: 100.00,
      payment: 'Promotional',
      balance: 300.50
    },
    {
      id: '5',
      date: '2024-01-01',
      type: 'purchase',
      description: 'Initial Credit Purchase',
      amount: 200.50,
      credits: 200.50,
      payment: 'Credit Card ***4567',
      balance: 200.50
    }
  ];

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'treatment': return 'bg-blue-100 text-blue-800';
      case 'purchase': return 'bg-green-100 text-green-800';
      case 'bonus': return 'bg-purple-100 text-purple-800';
      case 'refund': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const totalSpent = transactions
    .filter(t => t.type === 'treatment')
    .reduce((sum, t) => sum + t.amount, 0);

  const totalPurchased = transactions
    .filter(t => t.type === 'purchase')
    .reduce((sum, t) => sum + t.amount, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold font-juv-heading" style={{ color: '#5C322B' }}>
          Financial Ledger
        </h2>
        <Button variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Export Ledger
        </Button>
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Spent</p>
                <p className="text-2xl font-bold" style={{ color: '#5C322B' }}>${totalSpent}</p>
              </div>
              <DollarSign className="h-8 w-8" style={{ color: '#A55C52' }} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Purchased</p>
                <p className="text-2xl font-bold text-green-600">${totalPurchased}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Current Balance</p>
                <p className="text-2xl font-bold text-green-600">$275.50</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" style={{ color: '#A55C52' }} />
            <span>Complete Transaction History</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {transactions.map((transaction) => (
              <div key={transaction.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <Badge className={getTransactionColor(transaction.type)}>
                      {transaction.type}
                    </Badge>
                    <div className="flex items-center space-x-1 text-sm text-gray-600">
                      <Calendar className="h-3 w-3" />
                      <span>{new Date(transaction.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  <p className="font-medium mb-1">{transaction.description}</p>
                  <p className="text-sm text-gray-600">Payment: {transaction.payment}</p>
                </div>
                
                <div className="text-right">
                  <div className="space-y-1">
                    <p className="font-semibold">${transaction.amount}</p>
                    <p className={`text-sm ${
                      transaction.credits > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      Credits: {transaction.credits > 0 ? '+' : ''}${Math.abs(transaction.credits)}
                    </p>
                    <p className="text-sm text-gray-600">
                      Balance: ${transaction.balance}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PatientLedger;
